import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LoadingContainer } from '../components/ui/Spinner';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

function MistakeTypesPage() {
    const [mistakeTypes, setMistakeTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [deleteTypeId, setDeleteTypeId] = useState(null);

    // Form state
    const [form, setForm] = useState({
        name: '',
        description: '',
        color: '#f97316',
    });

    useEffect(() => {
        fetchMistakeTypes();
    }, []);

    const fetchMistakeTypes = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get('/api/mistake-types/');
            setMistakeTypes(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch mistake types:', err);
            setError('Failed to load mistake types');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingType) {
                await axios.patch(`/api/mistake-types/${editingType.id}/`, form);
            } else {
                await axios.post('/api/mistake-types/', form);
            }
            fetchMistakeTypes();
            handleCancel();
        } catch (err) {
            console.error('Failed to save mistake type:', err);
            setError(err.response?.data?.name?.[0] || 'Failed to save mistake type');
        }
    };

    const handleEdit = (type) => {
        setEditingType(type);
        setForm({
            name: type.name,
            description: type.description || '',
            color: type.color,
        });
        setShowAddModal(true);
    };

    const handleCancel = () => {
        setShowAddModal(false);
        setEditingType(null);
        setForm({
            name: '',
            description: '',
            color: '#f97316',
        });
        setError(null);
    };

    const handleDelete = async () => {
        if (!deleteTypeId) return;
        try {
            await axios.delete(`/api/mistake-types/${deleteTypeId}/`);
            fetchMistakeTypes();
            setDeleteTypeId(null);
        } catch (err) {
            console.error('Failed to delete mistake type:', err);
            setError(err.response?.data?.detail || 'Failed to delete mistake type');
            setDeleteTypeId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
                <LoadingContainer />
            </div>
        );
    }

    return (
        <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Mistake Types</h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Manage your custom mistake categories
                    </p>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add Type
                </button>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                    <p className="text-red-600 dark:text-red-400">{error}</p>
                </div>
            )}

            {/* Mistake Types Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mistakeTypes.map(type => (
                    <div
                        key={type.id}
                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-4 h-4 rounded-full"
                                    style={{ backgroundColor: type.color }}
                                ></div>
                                <h3 className="font-semibold text-gray-900 dark:text-white">{type.name}</h3>
                            </div>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => handleEdit(type)}
                                    className="p-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                </button>
                                {!type.is_default && (
                                    <button
                                        onClick={() => setDeleteTypeId(type.id)}
                                        className="p-1 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {type.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{type.description}</p>
                        )}
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-500">
                            <span>{type.mistakes_count || 0} mistakes</span>
                            {type.is_default && (
                                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                                    Default
                                </span>
                            )}
                        </div>
                    </div>
                ))}

                {mistakeTypes.length === 0 && (
                    <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
                        No mistake types yet. Add your first one!
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                            {editingType ? 'Edit Mistake Type' : 'Add Mistake Type'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({...form, name: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                    placeholder="e.g., Overplaying"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm({...form, description: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="3"
                                    placeholder="Optional description of this mistake type..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Color
                                </label>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="color"
                                        value={form.color}
                                        onChange={(e) => setForm({...form, color: e.target.value})}
                                        className="h-10 w-20 rounded cursor-pointer"
                                    />
                                    <input
                                        type="text"
                                        value={form.color}
                                        onChange={(e) => setForm({...form, color: e.target.value})}
                                        className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                        pattern="^#[0-9A-Fa-f]{6}$"
                                        placeholder="#f97316"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                                >
                                    {editingType ? 'Save Changes' : 'Add Type'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteTypeId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Mistake Type</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Are you sure you want to delete this mistake type? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteTypeId(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MistakeTypesPage;
